import { WorkflowConnection, WorkflowNode } from '../../types/workflow';

const HORIZONTAL_GAP = 280;
const VERTICAL_GAP = 190;
const START_X = 120;
const START_Y = 72;
const GRID_SIZE = 24;

const snap = (value: number) => Math.round(value / GRID_SIZE) * GRID_SIZE;

const getNodeClass = (node: WorkflowNode) => node.nodeClass ?? node.data?.nodeClass;

export const autoArrangeWorkflow = (nodes: WorkflowNode[], connections: WorkflowConnection[]): WorkflowNode[] => {
  if (nodes.length === 0) return nodes;

  const arranged = new Map<string, WorkflowNode>();
  const incomingCount = new Map<string, number>();
  const adjacency = new Map<string, string[]>();
  const parentIndices = new Map<string, number[]>();

  for (const node of nodes) {
    arranged.set(node.id, node);
    incomingCount.set(node.id, 0);
    adjacency.set(node.id, []);
    parentIndices.set(node.id, []);
  }

  for (const connection of connections) {
    if (connection.isLoopback) continue;
    if (!arranged.has(connection.sourceNodeId) || !arranged.has(connection.targetNodeId)) continue;
    adjacency.get(connection.sourceNodeId)?.push(connection.targetNodeId);
    incomingCount.set(connection.targetNodeId, (incomingCount.get(connection.targetNodeId) ?? 0) + 1);
  }

  const roots = nodes.filter((node) => getNodeClass(node) === 'trigger');
  const fallbackRoots = nodes.filter((node) => (incomingCount.get(node.id) ?? 0) === 0);
  const queue = [...(roots.length ? roots : fallbackRoots.length ? fallbackRoots : [nodes[0]])];
  const depthMap = new Map<string, number>();
  const visited = new Set<string>();

  for (const root of queue) depthMap.set(root.id, 0);

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;
    const currentDepth = depthMap.get(current.id) ?? 0;
    const currentIndex = nodes.findIndex((node) => node.id === current.id);
    visited.add(current.id);

    for (const nextId of adjacency.get(current.id) ?? []) {
      const nextDepth = currentDepth + 1;
      const previousDepth = depthMap.get(nextId);
      if (previousDepth === undefined || nextDepth > previousDepth) {
        depthMap.set(nextId, nextDepth);
      }
      parentIndices.get(nextId)?.push(currentIndex);
      if (!visited.has(nextId)) {
        const nextNode = arranged.get(nextId);
        if (nextNode) queue.push(nextNode);
      }
    }
  }

  for (const node of nodes) {
    if (!depthMap.has(node.id)) {
      depthMap.set(node.id, Math.max(...depthMap.values(), 0) + 1);
    }
  }

  const columns = new Map<number, WorkflowNode[]>();
  for (const node of nodes) {
    const depth = depthMap.get(node.id) ?? 0;
    const bucket = columns.get(depth) ?? [];
    bucket.push(node);
    columns.set(depth, bucket);
  }

  const orderedDepths = [...columns.keys()].sort((a, b) => a - b);
  const nextNodes: WorkflowNode[] = [];

  for (const depth of orderedDepths) {
    const bucket = columns.get(depth) ?? [];
    bucket.sort((left, right) => {
      const leftParents = parentIndices.get(left.id) ?? [];
      const rightParents = parentIndices.get(right.id) ?? [];
      const leftScore = leftParents.length
        ? leftParents.reduce((sum, index) => sum + index, 0) / leftParents.length
        : nodes.findIndex((node) => node.id === left.id);
      const rightScore = rightParents.length
        ? rightParents.reduce((sum, index) => sum + index, 0) / rightParents.length
        : nodes.findIndex((node) => node.id === right.id);
      return leftScore - rightScore;
    });

    const rowOffset = ((bucket.length - 1) * HORIZONTAL_GAP) / 2;
    bucket.forEach((node, index) => {
      nextNodes.push({
        ...node,
        position: {
          x: snap(START_X + index * HORIZONTAL_GAP - rowOffset + 600),
          y: snap(START_Y + depth * VERTICAL_GAP),
        },
      });
    });
  }

  return nextNodes.map((node) => arranged.get(node.id) ? node : node);
};

